import {assert} from 'Asserts';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import fetchContainersFromPlateWellPositions from 'notebook/structured-tables/util/fetchContainersFromPlateWellPositions';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {CreateProcessButtonQuery} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import createEmptyTaskTableFromSchema from 'processes/tables/createEmptyTaskTableFromSchema';

import {ProcessTaskStructuredTable} from '../ProcessTaskStructuredTableAdapter';
import findMatchingDestinationColumnIndex from './findMatchingDestinationColumnIndex';
import propagateContentFromSourceToDestinationTable from './propagateContentFromSourceToDestinationTable';

type ProcessTaskSchema = CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];
export default async function mapStructuredTableToTaskSchema(
  schema: ProcessTaskSchema,
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>> | null,
  selectorsById: SchemaFieldSelectorsDict
): Promise<{
  successfullyMappedColumn: boolean;
  taskTable: ProcessTaskStructuredTable;
  footerMessage?: string;
}> {
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  // Create task table that will be display in the task group creation modal
  const taskTable = createEmptyTaskTableFromSchema({
    name: schema.name,
    taskSchema: schema,
    tableLocation: ProcessTableLocation.CREATE_MODAL,
    locked: false,
    selectorsById,
  });
  let sourceTablePrimaryColumnSummary = adapter.getColumnSummariesForCreateProcessShortcut(structuredTable)
    ? adapter.getColumnSummariesForCreateProcessShortcut(structuredTable)[0]
    : null;
  if (sourceTablePrimaryColumnSummary != null) {
    // Fetch plate info
    let fetchedContainerInfo;
    if (sourceTablePrimaryColumnSummary.shouldFetchPlateInfo) {
      assert(
        sourceTablePrimaryColumnSummary.schemaIdConstraint == null,
        'Unexpected schema ID constraint on source column'
      );
      fetchedContainerInfo = await fetchContainersFromPlateWellPositions(
        adapter.getSampleInfosForRequest(structuredTable)
      );
      sourceTablePrimaryColumnSummary = {
        ...sourceTablePrimaryColumnSummary,
        schemaIdConstraint: fetchedContainerInfo?.schemaId ?? null,
      };
    }
    // Find the matching destination column in task table
    const destinationColumnIndex = findMatchingDestinationColumnIndex(
      sourceTablePrimaryColumnSummary,
      schema,
      taskTable
    );
    if (destinationColumnIndex != null) {
      // Propagate source column content to destination column
      return {
        successfullyMappedColumn: true,
        taskTable: propagateContentFromSourceToDestinationTable({
          sourceColumnIndex: sourceTablePrimaryColumnSummary.columnIndex,
          sourceTable: structuredTable,
          sourceComputedTable: computedTable,
          destinationColumnIndex,
          destinationTable: taskTable,
          containers: fetchedContainerInfo?.containers ?? null,
        }),
      };
    }
  }
  return {
    successfullyMappedColumn: false,
    taskTable,
    footerMessage: `No columns in "${schema.name}" were valid destinations for columns in the source table.`,
  };
}
